import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import { PAConstants, WMICRichTextUtil, DateUtil, WMICDriverEndorsementUtil } from 'wmic-pe-portals-utils-js';
import { WMICDriverEndorsementsDetailsComponent } from 'wmic-pe-capability-gateway-common-pa-react';
import { CheckboxField } from '@jutro/legacy/components';
import messages from './WMICPolicyDriverEndorsementCheckbox.messages';
import styles from './WMICPolicyDriverEndorsementCheckbox.module.scss';

function WMICDriverEndorsementsCheckbox(props) {
    const { id, driverEndorsement, driver, selectedVehicleId, baseState, rateAsOfDate, paLobDataVM } = props;
    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);

    const driverEndorsementPublicID = driverEndorsement?.publicID;

    const getDriverNameWithAccidentWaiverSuffix = () => {
        const hyphen = ' -';
        let purchaseDate;
        let inUse;

        if (driverEndorsementPublicID === PAConstants.accidentWaiverCovPublicId) {
            const accidentWaiverEndorsement = _.find(
                _.get(paLobDataVM, 'endorsements_Ext.accidentWaiverEndorsements_WMIC.value'),
                { driverPublicID: driver.publicID }
            );
            purchaseDate = accidentWaiverEndorsement?.purchaseDate;
            inUse = accidentWaiverEndorsement?.inUse;
        } else if (driverEndorsementPublicID === PAConstants.accidentWaiverMtcCovPublicId) {
            const accidentWaiverEndorsementMtc = _.find(
                _.get(paLobDataVM, 'endorsements_Ext.accidentWaiverMtcEndorsements_WMIC.value'),
                { driverPublicID: driver.publicID }
            );
            purchaseDate = accidentWaiverEndorsementMtc?.purchaseDate;
            inUse = accidentWaiverEndorsementMtc?.inUse;
        }

        let result = '';

        if (inUse === true) {
            result = `${hyphen} ${translator(messages.inUse)}`;
        } else if (inUse === false) {
            result = `${hyphen} ${translator(messages.notInUse)}`;
        }

        if (purchaseDate) {
            const purchaseDateString = intl.formatDate(DateUtil.localDateDTOToDate(purchaseDate), {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            });
            result += `${hyphen} ${translator(messages.accidentWaiverPurchaseDate)} ${purchaseDateString}`;
        }

        return driver.displayName + result;
    };

    const checked = useMemo(() => {
        if (
            driverEndorsementPublicID === PAConstants.accidentWaiverCovPublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.accidentWaiverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }

        if (
            driverEndorsementPublicID === PAConstants.accidentWaiverMtcCovPublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.accidentWaiverMtcEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }

        if (
            driverEndorsementPublicID === PAConstants.minorConvictionWaiver39bPublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.minorConvictionWaiverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }

        if (
            driverEndorsementPublicID === PAConstants.reducAmtForNamedCov_28A_PublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.excludedDriverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }

        if (
            driverEndorsementPublicID === PAConstants.reducAmtForNamedCov_28_PublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.reductionDriverEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
            })
        ) {
            return true;
        }

        if (
            driverEndorsementPublicID === PAConstants.article6ExcludedDriver_WMIC_PublicId &&
            _.find(_.get(paLobDataVM, 'endorsements_Ext.accidentWaiverMtcEndorsements_WMIC.value'), {
                driverPublicID: driver.publicID,
                vehiclePublicID: selectedVehicleId,
            })
        ) {
            return true;
        }

        return false;
    }, [driver.publicID, driverEndorsementPublicID, paLobDataVM, selectedVehicleId]);

    const getEndorsementLabel = () => {
        if (
            [
                PAConstants.reducAmtForNamedCov_28_PublicId,
                PAConstants.minorConvictionWaiver39bPublicId,
                PAConstants.reducAmtForNamedCov_28A_PublicId,
            ].includes(driverEndorsementPublicID)
        ) {
            return driver.displayName;
        }

        let label;
        switch (driverEndorsementPublicID) {
            case PAConstants.accidentWaiverCovPublicId:
            case PAConstants.accidentWaiverMtcCovPublicId:
                return getDriverNameWithAccidentWaiverSuffix();
            case PAConstants.article6ExcludedDriver_WMIC_PublicId:
                label = messages.article6Excluded;
                break;
            default:
                return null;
        }

        return WMICRichTextUtil.translateRichText(translator(label, { name: driver.displayName }));
    };

    return (
        <div
            className={cx('gw-pb-4', {
                [styles.checkboxDetails]: WMICDriverEndorsementUtil.shouldShowCheckboxDetailStyle(
                    driverEndorsementPublicID,
                    checked
                ),
            })}
        >
            <CheckboxField
                id={`${id}_checkbox`}
                className={cx('gw-pl-5', 'checkBoxReadOnly', 'wmicMasterInputCheckbox')}
                labelClassName={cx('wmicMasterInputFieldLabel')}
                label={getEndorsementLabel()}
                showInlineLabel
                readOnly
                value={checked}
                path={`${driverEndorsementPublicID}`}
            />

            <WMICDriverEndorsementsDetailsComponent
                checked={checked}
                driverEndorsementPublicID={driverEndorsementPublicID}
                existingEndorsements={WMICDriverEndorsementUtil.getExistingEndorsements(
                    driverEndorsement,
                    driver?.publicID,
                    paLobDataVM
                )}
                readOnly
                vehicles={paLobDataVM?.vehicleDTOs?.value}
            />
        </div>
    );
}

WMICDriverEndorsementsCheckbox.propTypes = {
    id: PropTypes.string.isRequired,
    driverEndorsement: PropTypes.shape({}).isRequired,
    driver: PropTypes.shape({}).isRequired,
    selectedVehicleId: PropTypes.string,
    baseState: PropTypes.string.isRequired,
    rateAsOfDate: PropTypes.shape({}).isRequired,
    paLobDataVM: PropTypes.shape({}).isRequired,
};

export default WMICDriverEndorsementsCheckbox;
